import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import type { Person } from "@/components/Combobox/PersonSelectorCombobox";
import { Category } from "@/components/Combobox/PersonSelectorCombobox/-query/type";
import Icon from "@/components/Icon/Icon";
import { WorkerSelect } from "@/components/Select/WorkerSelect";
import { LabelPerson } from "@/features/person/components/LabelPerson";
import { apiClient } from "@/lib/api/client";

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Props = CFormItemProps & {
  value?: string;
  allowEmpty?: boolean;
  readComponent?: ReactNode;
  onChange?: (personId?: string, person?: Person) => void;
  inputWidth?: number | string;
  inputClassName?: string;
  showProfileLink?: boolean;
  category?: Category;
};

/**
 * Card-based Form / PersonItem
 */
export const CFormPersonSelectItem = buildCFormItem<Props>((props: Props) => {
  const { data: selectedPerson } = useQuery({
    queryKey: ["peopleProfileData", props.value],
    queryFn: async () => {
      if (!props.value) return null;

      const personRes = await apiClient.GET("/v2/people", {
        params: { query: { personIdIn: [props.value] } },
      });

      // React Query v5ではundefinedを返すことができないため、nullを返す
      return personRes.data?.items[0] ?? null;
    },
    enabled: !!props.value,
  });

  return (
    <CFormItemBase
      {...props}
      readComponent={
        (props.readComponent ?? (props.value && selectedPerson)) ? (
          <div className="flex items-center gap-2">
            {props.showProfileLink && selectedPerson?.personId && (
              <Link
                to="/person/$id"
                target="_blank"
                rel="noopener noreferrer"
                params={{ id: selectedPerson.personId }}
              >
                <Icon iconName="SquareArrowOutUpRightIcon" />
              </Link>
            )}
            <LabelPerson
              name={selectedPerson?.name ?? ""}
              avatarUrl={selectedPerson?.avatarUrl ?? ""}
            />
          </div>
        ) : null
      }
      writeComponent={
        <WorkerSelect
          disabled={props.readonly}
          allowEmpty={props.allowEmpty}
          category={props.category}
          value={
            selectedPerson
              ? {
                  id: selectedPerson.personId,
                  name: selectedPerson.name,
                  nameInitials: selectedPerson.avatarUrl
                    ? ""
                    : (selectedPerson.name ?? "").slice(0, 2),
                  email: selectedPerson.primaryWorkerEmail ?? "",
                  avatarUrl: selectedPerson.avatarUrl ?? "",
                }
              : undefined
          }
          onChange={(person) => props.onChange?.(person?.id, person)}
          width={
            typeof props.inputWidth === "number" ? props.inputWidth : undefined
          }
        />
      }
    />
  );
});
